/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useRef } from 'react';
import { deleteVideo } from '../../entities/helpers/thankview-helper';
import { useRecorder } from './hooks/recorder-context';
import { useThankViewAuth } from 'apps/signal-video/contexts/thankview-auth-context';
import { Loading, ModalHeaderWithActions, Icon } from '@evertrue/et-components';
import EmptyStateMessage from 'components/lists/empty-state-message-tv';
import VideoList from './library-components/video-list';
import EverTrue from 'app';
import ControlTools from './library-components/control-tools';
import { useSignalVideoModalContext } from 'apps/signal-video/contexts/use-signal-video-modal-context';
import PropTypes from 'prop-types';
import useFetchThankviewVideos from './hooks/use-fetch-thankview-videos';

const sortTypes = [
  { label: 'Date Created', value: 'created_at', defaultOrder: 'desc' },
  { label: 'Date Modified', value: 'updated_at', defaultOrder: 'desc' },
  { label: 'Title', value: 'name', defaultOrder: 'asc' },
  { label: 'Duration', value: 'video_length', defaultOrder: 'asc' },
];

function ThankviewLibrary({ onCopyVideo }) {
  const { updateState, stopAndResetRecorder } = useRecorder();
  const { handleCloseThankviewModal, handleChangeModalView } = useSignalVideoModalContext();
  const { getValidToken, thankViewUserId } = useThankViewAuth();
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [filters, setFilters] = useState({
    searchText: '',
    sortSelected: sortTypes[0],
    isAscOrder: sortTypes[0].defaultOrder === 'asc',
  });
  const [startDate, setStartDate] = useState('2000-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const observerTarget = useRef(null);
  const { videos, loading, totalCount, resetVideos } = useFetchThankviewVideos(
    getValidToken,
    thankViewUserId,
    startDate,
    endDate,
    filters,
    observerTarget
  );

  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    resetVideos();
  };

  const toggleVideoSelection = useCallback(
    (videoId) => {
      setSelectedVideos((prev) => (prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]));
    },
    [selectedVideos.length, updateState]
  );

  const deleteVideoEntry = useCallback(
    (videoId) => {
      try {
        EverTrue.Alert.confirm(
          {
            headline: 'Delete Video',
            content: 'Are you sure you want to delete this video?',
          },
          async (did_confirm) => {
            if (did_confirm) {
              EverTrue.track.set('signal_video', { type: 'signal_video_delete' });
              const token = await getValidToken();
              await deleteVideo(videoId, token);
              setSelectedVideos([]);
              resetVideos(true);
            }
          }
        );
      } catch (err) {
        console.error('Error deleting video:', err);
      }
    },
    [getValidToken, resetVideos]
  );

  const handleControlChange = useCallback(
    (type, value) => {
      setFilters((prev) => {
        const newFilters = { ...prev, [type]: value };
        resetVideos();
        return newFilters;
      });
    },
    [resetVideos]
  );

  const renderResults = () => {
    if (totalCount === 0) {
      return (
        <>
          <EmptyStateMessage icon="logo" iconKit="tv" text="No Signal Videos Found" />
          {loading && <Loading />}
        </>
      );
    }

    return (
      <>
        <VideoList
          videos={videos}
          onToggleSelection={toggleVideoSelection}
          onDeleteVideo={deleteVideoEntry}
          onCopyVideo={onCopyVideo}
        />
        <p className="signal-video-powered-by">
          Signal Video <i>Powered by ThankView</i>
        </p>
        {loading && <Loading />}
        <div ref={observerTarget} style={{ height: '1px' }}></div>
      </>
    );
  };

  return (
    <>
      <ModalHeaderWithActions
        closeModal={() => handleCloseThankviewModal(stopAndResetRecorder)}
        title="Video Library"
        onSubmit={() => {
          handleChangeModalView('recorder');
          updateState({ videoUrl: '' });
        }}
        submitLabel={
          <>
            <Icon icon="tv-video" iconKit="tv" className="tv-btn-icon" />
            New Video
          </>
        }
        closeLabel="Cancel"
      />
      <div id="video-library-parent-container" className="video-library-parent-container">
        <ControlTools
          searchText={filters.searchText}
          setSearchText={(text) => handleControlChange('searchText', text)}
          sortSelected={filters.sortSelected}
          setSortSelected={(value) => handleControlChange('sortSelected', value)}
          isAscOrder={filters.isAscOrder}
          setIsAscOrder={(value) => handleControlChange('isAscOrder', value)}
          onDateRangeChange={handleDateRangeChange}
          totalCount={totalCount}
        />
        <div className="tv-library-container">{renderResults()}</div>
      </div>
    </>
  );
}

ThankviewLibrary.propTypes = {
  onCopyVideo: PropTypes.func,
};

export default React.memo(ThankviewLibrary);
